import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EQUIPMENT_STATUS_COLOR, type IEquipmentAsset, type StatusFilter } from '../equipment.types';
import styles from './AssetRegistry.module.css';
import { EquipmentIcon } from '../ui/EquipmentIcon/EquipmentIcon';
import { MaintenanceFlag } from '../ui/MaintenanceFlag/MaintenanceFlag';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { SearchIconSm, WarningTriangleIcon } from '@utils/icons';

interface IAssetRegistryProps {
  assets: IEquipmentAsset[];
  filteredAssets: IEquipmentAsset[];
  search: string;
  onSearchChange: (value: string) => void;
  typeFilter: string | null; // null = "all". Other values are concrete `typeNick` strings
  onTypeFilterChange: (value: string | null) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (value: StatusFilter) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  isLoading: boolean;
}

export function AssetRegistry({
  assets,
  filteredAssets,
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  selectedId,
  onSelect,
  isLoading,
}: IAssetRegistryProps) {
  const { t } = useTranslation();

  const counts = useMemo(() => {
    const total = assets.length;
    const active = assets.filter((a) => a.status === 'active').length;
    const idle = assets.filter((a) => a.status === 'idle').length;
    const needsMaintenance = assets.filter((a) => a.needsMaintenance).length;
    return { total, active, idle, needsMaintenance };
  }, [assets]);

  // Type chips come from the response, so a type Trusstor adds shows up without a redeploy.
  const typeOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const asset of assets) seen.add(asset.typeNick);
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  return (
    <aside className={styles.panel}>
      <div className={styles.titleRow}>
        <EquipmentIcon/>
        <h2 className={styles.title}>{t('equipment.registry.title')}</h2>
      </div>

      <div className={styles.stats}>
        <Stat
          tone="neutral"
          value={counts.total}
          label={t('equipment.status.total')}
          pressed={statusFilter === null}
          onClick={() => onStatusFilterChange(null)}
        />
        <Stat
          tone="active"
          value={counts.active}
          label={t('equipment.status.active')}
          pressed={statusFilter === 'active'}
          onClick={() => onStatusFilterChange(statusFilter === 'active' ? null : 'active')}
        />
        <Stat
          tone="idle"
          value={counts.idle}
          label={t('equipment.status.idle')}
          pressed={statusFilter === 'idle'}
          onClick={() => onStatusFilterChange(statusFilter === 'idle' ? null : 'idle')}
        />
      </div>

      {counts.needsMaintenance > 0 && (
        <button
          type="button"
          className={styles.warnBanner}
          aria-pressed={statusFilter === 'needsMaintenance'}
          onClick={() => onStatusFilterChange(statusFilter === 'needsMaintenance' ? null : 'needsMaintenance')}
        >
          <WarningTriangleIcon />
          {t('equipment.registry.maintenanceWarning', { count: counts.needsMaintenance })}
        </button>
      )}

      <label className={styles.searchField}>
        <SearchIconSm />
        <input
          type="search"
          className={styles.searchInput}
          placeholder={t('equipment.registry.searchPlaceholder')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </label>

      <div className={styles.chips} role="group">
        <button
          type="button"
          className={styles.chip}
          data-active={typeFilter === null}
          onClick={() => onTypeFilterChange(null)}
        >
          {t('equipment.category.all')}
        </button>
        {typeOptions.map((label) => (
          <button
            key={label}
            type="button"
            className={styles.chip}
            data-active={typeFilter === label}
            onClick={() => onTypeFilterChange(label)}
          >
            {label}
          </button>
        ))}
      </div>

      <ScrollArea className={styles.listScroll}>
        <ul className={styles.list}>
          {isLoading && assets.length === 0 && (
            <li className={styles.empty}>{t('equipment.registry.loading')}</li>
          )}
          {!isLoading && filteredAssets.length === 0 && (
            <li className={styles.empty}>
              {assets.length === 0
                ? t('equipment.registry.empty')
                : t('equipment.registry.noMatches')}
            </li>
          )}
          {filteredAssets.map((asset) => {
            const isSelected = asset.equipmentId === selectedId;
            const location = asset.floorNick ?? asset.areaNick ?? '';
            return (
              <li key={asset.equipmentId}>
                <button
                  type="button"
                  className={styles.row}
                  data-selected={isSelected}
                  onClick={() => onSelect(isSelected ? null : asset.equipmentId)}
                >
                  <StatusDot color={EQUIPMENT_STATUS_COLOR[asset.status]} animate={asset.status === 'active'} className={styles.rowDotOffset} />
                  <div className={styles.rowBody}>
                    <div className={styles.rowTop}>
                      <span className={styles.assetName}>{asset.name}</span>
                      {asset.needsMaintenance && <MaintenanceFlag />}
                      <span className={styles.statusText} data-status={asset.status}>
                        {t(`equipment.statusLabel.${asset.status}`)}
                      </span>
                    </div>
                    <div className={styles.rowBottom}>
                      <span className={styles.assetMeta}>
                        {asset.typeNick}
                        {asset.company ? ` · ${asset.company}` : ''}
                      </span>
                      <span className={styles.assetLocation}>{location}</span>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </ScrollArea>
    </aside>
  );
}

interface IStatProps {
  tone: 'neutral' | 'active' | 'idle';
  value: number;
  label: string;
  pressed: boolean;
  onClick: () => void;
}

function Stat({ tone, value, label, pressed, onClick }: IStatProps) {
  return (
    <button type="button" className={styles.stat} data-tone={tone} aria-pressed={pressed} onClick={onClick}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </button>
  );
}
